import {
  menu,
  type MenuConfig,
} from '@blocksuite/affine-components/context-menu';

import type { PropertyConfigMenuContext } from '../../core/property/renderer.js';
import type { AggFn } from './evaluator.js';
import type { FormulaPropertyData } from './types.js';

const AGG_FNS: { fn: AggFn; label: string }[] = [
  { fn: 'sum', label: 'Sum' },
  { fn: 'avg', label: 'Average' },
  { fn: 'min', label: 'Min' },
  { fn: 'max', label: 'Max' },
  { fn: 'count', label: 'Count' },
];

const NUMBER_FORMATS: {
  value: FormulaPropertyData['format'];
  label: string;
}[] = [
  { value: 'number', label: 'Number' },
  { value: 'numberWithCommas', label: 'Number with commas' },
  { value: 'percent', label: 'Percent' },
  { value: 'currencyUSD', label: 'US Dollar' },
  { value: 'currencyEUR', label: 'Euro' },
  { value: 'currencyCNY', label: 'Yuan' },
  { value: 'currencyRUB', label: 'Russian Ruble' },
];

export const buildFormulaConfigMenu = (
  ctx: PropertyConfigMenuContext<FormulaPropertyData>
): MenuConfig[] => {
  const { property } = ctx;
  const data = property.data$.value;

  // Other columns on this view, used to insert {colId} tokens / resolve names.
  const cols = property.view.propertiesRaw$.value.filter(
    p => p.id !== property.id
  );
  const idToName = (id: string) =>
    cols.find(c => c.id === id)?.name$.value ?? id;
  const nameToId = (name: string) =>
    cols.find(c => c.name$.value === name.trim())?.id;

  // Expression is stored with stable {colId} tokens; show it with {colName}.
  const displayExpr = data.expression.replace(
    /\{([^}]+)\}/g,
    (_, id: string) => `{${idToName(id)}}`
  );

  const appendToken = (token: string) =>
    property.dataUpdate(d => ({
      ...d,
      expression: d.expression ? `${d.expression.trimEnd()} ${token}` : token,
    }));

  const exprInput = menu.input({
    placeholder: 'e.g. {Кол-во} * {Цена за ед.}',
    initialValue: displayExpr,
    onComplete: value => {
      // Translate display names back to stable ids; leave unknowns as-is so
      // the user can see/fix typos.
      const stored = value.replace(
        /\{([^}]+)\}/g,
        (_, name: string) => `{${nameToId(name) ?? name.trim()}}`
      );
      property.dataUpdate(d => ({ ...d, expression: stored }));
    },
  });

  const insertColumnSubMenu = menu.subMenu({
    name: 'Insert column',
    options: {
      items:
        cols.length === 0
          ? [menu.action({ name: 'No other columns', select: () => {} })]
          : cols.map(c =>
              menu.action({
                name: c.name$.value || 'Untitled',
                select: () => appendToken(`{${c.id}}`),
              })
            ),
    },
  });

  const insertAggregateSubMenu = menu.subMenu({
    name: 'Insert aggregate (whole column)',
    options: {
      items: AGG_FNS.map(({ fn, label }) =>
        menu.subMenu({
          name: label,
          options: {
            items:
              cols.length === 0
                ? [menu.action({ name: 'No other columns', select: () => {} })]
                : cols.map(c =>
                    menu.action({
                      name: c.name$.value || 'Untitled',
                      select: () => appendToken(`${fn}({${c.id}})`),
                    })
                  ),
          },
        })
      ),
    },
  });

  const formatSubMenu = menu.subMenu({
    name: `Format: ${
      NUMBER_FORMATS.find(f => f.value === data.format)?.label ?? data.format
    }`,
    options: {
      items: NUMBER_FORMATS.map(f =>
        menu.action({
          name: f.label,
          isSelected: data.format === f.value,
          select: () => property.dataUpdate(d => ({ ...d, format: f.value })),
        })
      ),
    },
  });

  const decimalSubMenu = menu.subMenu({
    name: `Decimals: ${data.decimal}`,
    options: {
      items: [0, 1, 2, 3, 4].map(n =>
        menu.action({
          name: String(n),
          isSelected: data.decimal === n,
          select: () => property.dataUpdate(d => ({ ...d, decimal: n })),
        })
      ),
    },
  });

  // Display-only affixes: any symbol/text wrapped around the number, e.g. `₽`,
  // `kg`, `°C`. Stored as-is; the numeric value is unaffected.
  const prefixInput = menu.input({
    placeholder: 'Prefix, e.g. $',
    initialValue: data.prefix ?? '',
    onComplete: value =>
      property.dataUpdate(d => ({ ...d, prefix: value || undefined })),
  });

  const suffixInput = menu.input({
    placeholder: 'Suffix, e.g. ₽',
    initialValue: data.suffix ?? '',
    onComplete: value =>
      property.dataUpdate(d => ({ ...d, suffix: value || undefined })),
  });

  return [
    menu.group({ items: [exprInput] }),
    menu.group({ items: [insertColumnSubMenu, insertAggregateSubMenu] }),
    menu.group({ items: [formatSubMenu, decimalSubMenu] }),
    menu.group({ items: [prefixInput, suffixInput] }),
  ];
};
